import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import { Drawer } from "@salesapp/ui";

export type AdminNavItem = {
  to: string;
  label: string;
  end?: boolean;
};

export const PLATFORM_ADMIN_NAV: AdminNavItem[] = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/website", label: "Website" },
  { to: "/customers", label: "Customers" },
  { to: "/recycle-bin", label: "Recycle bin" },
  { to: "/payments", label: "Billing" },
  { to: "/mail", label: "Email" },
  { to: "/email-errors", label: "Email errors" },
  { to: "/marketing", label: "Marketing" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/platform", label: "App Settings" },
  { to: "/ai", label: "AI" },
  { to: "/security", label: "App Security" },
  { to: "/dependency-security", label: "Dependency security" },
  { to: "/monitoring", label: "Monitoring" },
  { to: "/backups", label: "Backups" },
  { to: "/capacity", label: "AWS capacity" },
  { to: "/storage-access", label: "Storage access" },
  { to: "/privacy", label: "Privacy" },
  { to: "/threats", label: "Threats" },
  { to: "/users", label: "Users" },
  { to: "/activity", label: "Staff activity" },
  { to: "/online-users", label: "Current online users" },
  { to: "/maintenance", label: "Maintenance" },
];

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="admin-nav-menu-icon" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function itemActive(pathname: string, item: AdminNavItem): boolean {
  if (item.end || item.to === "/") return pathname === "/";
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function AdminNav({ label, items }: { label: string; items: AdminNavItem[] }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const active = items.find((item) => itemActive(location.pathname, item));

  return (
    <>
      <div className="admin-nav-bar">
        <strong className="admin-nav-current">{active?.label ?? label}</strong>
        <button
          type="button"
          className="admin-nav-menu-btn"
          aria-label={`Open ${label} menu`}
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <MenuIcon />
          <span>Menu</span>
        </button>
      </div>
      <nav className="admin-nav" aria-label={label}>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => (isActive ? "is-active" : undefined)}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <Drawer isOpen={open} onClose={() => setOpen(false)} title={label} side="left">
        <nav className="admin-nav-drawer" aria-label={label}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `admin-nav-drawer-link${isActive ? " is-active" : ""}`}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </Drawer>
    </>
  );
}
